import type { ReactNode } from 'react';
import type { PtoVolumeMark, PtoVolumePosition } from '@shared/api';
import type { RowEditAction } from '../rdEdits/RowEditDialog';
import type { RdVolumeEditAccess } from '../rdEdits/useRdVolumeEditAccess';
import { EditBadgeButton, EditNote, RowEditButtons } from '../rdEdits/VolumeEditCells';
import type { GapRowFlags } from '../rdGaps/logic';
import { NameCell, NormMark, SectionCell, SourceCell } from './cells';
import { CodeCell } from './CodeCell';
import { fmtConf } from './format';
import { GapBadge } from './GapBadge';
import { GROUP_SEP, groupPath, type RdPos } from './groups';
import { DecisionCell, StatusCell } from './HitlCells';
import { QtyCell } from './QtyCell';
import { RowNoCell } from './RowNoCell';
import { SheetCheckCell, SumMark } from './SelectionCells';
import { SystemCell } from './SystemCell';

/** Данные экрана, нужные ячейкам: права, обработчики и данные ссылки на строку.
 *  Новые данные для граф добавляются сюда и заполняются в `PtoVolumes`. */
export interface VolumeCellContext {
  canWrite: boolean;
  onAnnotate: (id: string, decision: string) => void;
  /** Шифр комплекта для «Скопировать ссылку»; null — ещё не загружен. */
  docCode: string | null;
  projectId: string;
  revisionId: string;
  /** Признаки отбора по id позиции (`…/volume-selection`); пусто — не загружены. */
  marks: ReadonlyMap<string, PtoVolumeMark>;
  /** Все позиции ревизии по id — для подписей учтённых строк. */
  byId: ReadonlyMap<string, PtoVolumePosition>;
  /** Открыть лист строки в окне исходного листа РД. */
  onOpenSheet: (p: PtoVolumePosition) => void;
  /** В ревизии есть разметка систем (ОВ, ВК, НВ, НК): графа «Система». */
  hasSystems?: boolean;
  /** В ревизии без систем есть группы таблиц (`raw.group`): графа «Группа». */
  hasGroups?: boolean;
  /** «Поз.» РД строк по id — графа «Шифр» (вопрос 10, вариант «б»). */
  rdPos?: ReadonlyMap<string, RdPos>;
  /** Значки «⚠ N» анализа комплектности у строк ВОР (№7); null — анализ
   *  выключен для проекта или роли. */
  gaps: GapRowFlags | null;
  /** Правки РД «Инженер+» (№8): флаг проекта, право правки, справочники. */
  rdEdits: RdVolumeEditAccess;
  /** Открыть окно правки строки (или её историю). */
  onOpenEdit: (p: PtoVolumePosition, action: RowEditAction) => void;
}

/** Графа таблицы позиций: заголовок и ячейка описаны в одном месте. */
export interface VolumeColumn {
  key: string;
  title: string;
  /** Числовая графа: выравнивание вправо (класс `num` у th и td). */
  num?: boolean;
  /** Длинный заголовок переносится по словам (класс `wrap` у th). */
  wrap?: boolean;
  /** Графа видна не всем; без условия — видна всегда. */
  visible?: (ctx: VolumeCellContext) => boolean;
  cell: (p: PtoVolumePosition, ctx: VolumeCellContext) => ReactNode;
}

/** Графы слева направо: порядок массива = порядок граф в таблице. */
export const VOLUME_COLUMNS: VolumeColumn[] = [
  {
    key: 'row_no',
    title: '№',
    cell: (p, ctx) => (
      <RowNoCell
        position={p}
        docCode={ctx.docCode}
        projectId={ctx.projectId}
        revisionId={ctx.revisionId}
        onOpenSheet={ctx.onOpenSheet}
      />
    ),
  },
  {
    key: 'name',
    title: 'Наименование',
    // Значки «⚠ N» анализа комплектности (№7) — рядом с наименованием; пометка
    // правки №8 («было → стало», кто и когда; «по документу № …») — видна всем,
    // кто видит экран.
    cell: (p, ctx) => (
      <>
        <NameCell position={p} badge={<GapBadge position={p} gaps={ctx.gaps} />} />
        <EditNote position={p} access={ctx.rdEdits.access} />
      </>
    ),
  },
  // Пометка у ячейки, изменённой нормализацией на выходе (`raw.normalized`).
  {
    key: 'code',
    title: 'Шифр',
    cell: (p, ctx) => (
      <>
        <CodeCell position={p} pos={ctx.rdPos?.get(p.id)} />
        <NormMark position={p} field="code" />
      </>
    ),
  },
  {
    key: 'unit',
    title: 'Ед.',
    cell: (p) => (
      <>
        {p.unit ?? '—'}
        <NormMark position={p} field="unit" />
      </>
    ),
  },
  {
    key: 'quantity',
    title: 'Кол-во',
    num: true,
    // Под числом РД — площадь покрытия ЛКМ с учётом слоёв и оценка по нормам.
    cell: (p, ctx) => (
      <>
        <QtyCell position={p} ctx={ctx} />
        <NormMark position={p} field="quantity" />
      </>
    ),
  },
  { key: 'section', title: 'Раздел', cell: (p) => <SectionCell position={p} /> },
  {
    key: 'system',
    title: 'Система',
    visible: (ctx) => Boolean(ctx.hasSystems),
    cell: (p) => <SystemCell position={p} />,
  },
  {
    key: 'group',
    title: 'Группа',
    wrap: true,
    visible: (ctx) => Boolean(ctx.hasGroups) && !ctx.hasSystems,
    cell: (p) => groupPath(p.raw).join(GROUP_SEP) || '—',
  },
  {
    key: 'source',
    title: 'Источник',
    cell: (p, ctx) => <SourceCell position={p} onOpenSheet={ctx.onOpenSheet} />,
  },
  {
    key: 'sheet_check',
    title: 'Сверка с листами РД',
    wrap: true,
    cell: (p, ctx) => <SheetCheckCell position={p} ctx={ctx} />,
  },
  { key: 'confidence', title: 'Увер.', num: true, cell: (p) => fmtConf(p.confidence) },
  {
    key: 'status',
    title: 'Статус',
    cell: (p, ctx) => (
      <>
        <SumMark position={p} ctx={ctx} />
        <StatusCell position={p} />
        <EditBadgeButton
          position={p}
          access={ctx.rdEdits.access}
          onOpen={() => ctx.onOpenEdit(p, 'history')}
        />
      </>
    ),
  },
  {
    key: 'decision',
    title: 'Решение',
    visible: (ctx) => ctx.canWrite,
    cell: (p, ctx) => (
      <DecisionCell
        position={p}
        canWrite={ctx.canWrite}
        rights={ctx.rdEdits}
        onAnnotate={ctx.onAnnotate}
        onDispute={() => ctx.onOpenEdit(p, 'dispute')}
        extra={
          <RowEditButtons
            position={p}
            rights={ctx.rdEdits}
            onOpen={(action) => ctx.onOpenEdit(p, action)}
          />
        }
      />
    ),
  },
];

/** Графы, видимые при данных правах. */
export function visibleColumns(ctx: VolumeCellContext): VolumeColumn[] {
  return VOLUME_COLUMNS.filter((c) => !c.visible || c.visible(ctx));
}
